/**
 * calculateSuggestedOccurrences
 *
 * Pure function. Returns a UI-only suggestion for how many recurring visits
 * remain in a season window starting from a given date and cadence.
 *
 * Contract — locked, do not relax:
 *   • This is ONLY a UI suggestion. Operator override always wins; the
 *     returned `count` is the seed value for a `totalOccurrences` input,
 *     never an enforced limit.
 *   • The recurrence engine (`buildForecast.js`, `servicePlanSeeding.js`)
 *     does NOT call this. Programs without season fields produce a `null`
 *     suggestion and the operator types the count themselves (existing
 *     behavior preserved).
 *   • Season fields are "MM-DD" strings (no year), so the same program
 *     reuses across calendar years.
 *
 * @param {Object}  opts
 * @param {string=} opts.startDate            "YYYY-MM-DD" (operator-picked); falls back to today
 * @param {number}  opts.intervalDays         positive int (e.g., 14)
 * @param {string=} opts.seasonStartMonthDay  "MM-DD" or null
 * @param {string=} opts.seasonEndMonthDay    "MM-DD" or null
 *
 * @returns {Object|null}
 *   null  → no suggestion possible (no season window, missing/invalid interval)
 *   else  → {
 *     count:           number,                              // suggested totalOccurrences
 *     seasonStart:     'YYYY-MM-DD',                        // concrete-year resolved start
 *     seasonEnd:       'YYYY-MM-DD',                        // concrete-year resolved end
 *     effectiveStart:  'YYYY-MM-DD',                        // date count is computed from
 *     reason:          'normal' | 'before_season' | 'past_season',
 *   }
 *
 * `reason` semantics:
 *   normal         → operator's start date is within the season; count from it
 *   before_season  → operator's start date precedes the season; counted from
 *                    the season start (the visits operator can deliver in
 *                    the upcoming season instance)
 *   past_season    → operator's start date is past the season end; suggest
 *                    0 so the operator notices and re-books under next year
 */

// ─── Helpers ───────────────────────────────────────────────────────────────

const MD_RE = /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

function isValidMonthDay(md) {
  return typeof md === 'string' && MD_RE.test(md);
}

function parseYMD(s) {
  if (typeof s !== 'string' || s.length < 10) return null;
  const [y, m, d] = s.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
}

function formatYMD(date) {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function mdInYear(monthDay, year) {
  const [m, d] = monthDay.split('-').map(Number);
  // Feb-29 in non-leap years → clamp to Feb-28. This keeps the suggestion
  // deterministic without affecting the canonical "MM-DD" stored on the
  // program.
  const last = new Date(Date.UTC(year, m, 0)).getUTCDate();
  return new Date(Date.UTC(year, m - 1, Math.min(d, last)));
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function diffDays(later, earlier) {
  return Math.floor((later.getTime() - earlier.getTime()) / MS_PER_DAY);
}

// ─── Main ──────────────────────────────────────────────────────────────────

export function calculateSuggestedOccurrences({
  startDate,
  intervalDays,
  seasonStartMonthDay,
  seasonEndMonthDay,
} = {}) {
  const hasStartMd = isValidMonthDay(seasonStartMonthDay);
  const hasEndMd   = isValidMonthDay(seasonEndMonthDay);
  if (!hasStartMd && !hasEndMd) return null;

  const interval = parseInt(intervalDays, 10);
  if (!Number.isInteger(interval) || interval < 1) return null;

  // Resolve start (operator-picked or today, UTC midnight)
  let start = parseYMD(startDate);
  if (!start) {
    const now = new Date();
    start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  }
  const startYear = start.getUTCFullYear();

  // Default open ends so partial configurations still produce a sensible suggestion
  const startMd = hasStartMd ? seasonStartMonthDay : '01-01';
  const endMd   = hasEndMd   ? seasonEndMonthDay   : '12-31';

  // Wrap detection (e.g., Nov-01 → Mar-31). Pure string compare works for
  // zero-padded "MM-DD" — both date components sort lexicographically.
  const wraps = startMd > endMd;

  let sStart;
  let sEnd;
  let effectiveStart;
  let reason;

  if (!wraps) {
    // Contiguous in-calendar-year window
    sStart = mdInYear(startMd, startYear);
    sEnd   = mdInYear(endMd,   startYear);

    if (start.getTime() > sEnd.getTime()) {
      return {
        count: 0,
        seasonStart:    formatYMD(sStart),
        seasonEnd:      formatYMD(sEnd),
        effectiveStart: formatYMD(start),
        reason: 'past_season',
      };
    }
    if (start.getTime() < sStart.getTime()) {
      effectiveStart = sStart;
      reason = 'before_season';
    } else {
      effectiveStart = start;
      reason = 'normal';
    }
  } else {
    // Year-wrapping window. The season instance containing `start` is
    // either (startYear-1 → startYear) or (startYear → startYear+1).
    const prevStart = mdInYear(startMd, startYear - 1);
    const prevEnd   = mdInYear(endMd,   startYear);
    const curStart  = mdInYear(startMd, startYear);
    const curEnd    = mdInYear(endMd,   startYear + 1);

    if (start.getTime() >= prevStart.getTime() && start.getTime() <= prevEnd.getTime()) {
      sStart = prevStart;
      sEnd = prevEnd;
      effectiveStart = start;
      reason = 'normal';
    } else if (start.getTime() >= curStart.getTime() && start.getTime() <= curEnd.getTime()) {
      sStart = curStart;
      sEnd = curEnd;
      effectiveStart = start;
      reason = 'normal';
    } else {
      // Falls in the gap between two season instances (e.g., a Nov–Mar
      // season, start date is in July). Snap forward to the next start.
      sStart = curStart;
      sEnd = curEnd;
      effectiveStart = sStart;
      reason = 'before_season';
    }
  }

  // count = floor((seasonEnd - effectiveStart) / interval) + 1 — inclusive
  // because a visit happens ON effectiveStart itself.
  const daysAvail = diffDays(sEnd, effectiveStart);
  const count = Math.max(0, Math.floor(daysAvail / interval) + 1);

  return {
    count,
    seasonStart:    formatYMD(sStart),
    seasonEnd:      formatYMD(sEnd),
    effectiveStart: formatYMD(effectiveStart),
    reason,
  };
}

// ─── Display helper (UI-only) ──────────────────────────────────────────────

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export function formatSeasonEndShort(monthDay) {
  if (!isValidMonthDay(monthDay)) return '';
  const [m, d] = monthDay.split('-').map(Number);
  return `${MONTH_NAMES[m - 1]} ${d}`;
}
